import { useCallback, useMemo } from 'react'
import { useGetAppFeatureMap } from 'src/api/app-features'
import { AppFeatureScope } from 'src/constants/appFeatureScope'
import { isAppFeatureEnabled, isAppFeatureIntegrationEnabled } from 'src/helpers/appFeatureHelper'
import { normalizeAppFeatureState } from 'src/helpers/appFeatureState'
import { useFeatureFlagsCtx } from 'src/hooks/context-hooks'
import { useCustomerAdminCustomerId } from 'src/hooks/useCustomerAdminCustomerId'

/**
 * Resolves feature flags for the current UI context.
 * - global: signed-in user map (any assigned customer for non-admins)
 * - customer: per-customer map from super-admin assignments
 * - auto: customer scope in client admin routes, otherwise global
 */
export function useAppFeatureFlags(
  explicitCustomerId?: number,
  scope: AppFeatureScope = AppFeatureScope.Auto,
) {
  const routeCustomerId = useCustomerAdminCustomerId()
  const globalCtx = useFeatureFlagsCtx()

  const customerId = useMemo((): number | undefined => {
    if (scope === AppFeatureScope.Global) {
      return undefined
    }
    const raw = explicitCustomerId ?? routeCustomerId
    return raw != null && Number.isFinite(raw) && raw > 0 ? raw : undefined
  }, [explicitCustomerId, routeCustomerId, scope])

  const useCustomerScope = customerId != null

  const { data, isLoading: customerMapLoading } = useGetAppFeatureMap(customerId ? [customerId] : undefined, {
    enabled: useCustomerScope,
  })

  const customerState = normalizeAppFeatureState(data)
  const customerIsEnabled = useCallback(
    (key: string) => isAppFeatureEnabled(key, customerState.features),
    [customerState.features],
  )
  const customerIsIntegrationEnabled = useCallback(
    (integrationKey: string) => isAppFeatureIntegrationEnabled(integrationKey, customerState.integrations),
    [customerState.integrations],
  )

  if (useCustomerScope) {
    return {
      flags: customerState.features,
      integrations: customerState.integrations,
      isLoading: customerMapLoading,
      isEnabled: customerIsEnabled,
      isIntegrationEnabled: customerIsIntegrationEnabled,
      customerId,
      scope: AppFeatureScope.Customer,
    }
  }

  return {
    flags: globalCtx.flags,
    integrations: globalCtx.integrations,
    isLoading: globalCtx.isLoading,
    isEnabled: globalCtx.isEnabled,
    isIntegrationEnabled: globalCtx.isIntegrationEnabled,
    customerId: undefined,
    scope: AppFeatureScope.Global,
  }
}
